// Legal pages + contact — English (reference language).
//
// The figures (refund window, cooling-off delay, trial length) are NOT
// duplicated here: they come from lib/legal.js, so a single edit changes
// every language at once. Only the wording lives in this file.
const legal = {
  // Money is written the way each market writes it.
  betAmount: '$100',
  supportHours: '9am – 11pm, 7 days a week',
  supportDelay: 'under an hour',

  shell: {
    updated: d => `Last updated: ${d}`
  },

  contact: {
    title: 'Write to us',
    updated: null,
    intro: (delay, hours) =>
      `A question before you start, an installation problem, a refund request:
       we answer in ${delay}, ${hours}.`,
    emailN: 'Email',
    emailS: 'The safest route for anything touching your account or a payment.',
    whatsappS: 'The fastest, for everything: setup, billing, cancellation.',
    openChat: 'Open the chat',
    waMsg: 'Hi, I have a question about SELFCNTRL.',
    fastT: "What we'll answer quickly",
    fastP: days => `An installation problem on your device, a profile that
      won't lock, an invoice, a cancellation, a refund request within
      ${days} days: those are requests we handle straight away.`,
    cantT: "What we won't be able to do",
    cantP1: hours => <><b>Remove your lock faster than the {hours}-hour delay.</b> That delay is the heart of the product: if support could bypass it, one message on a weak evening would undo everything. We will not make an exception, however hard you push — and that is precisely what you came to buy.</>,
    cantP2: `We also can't tell you what you looked at: we keep no browsing
             history whatsoever. That isn't a stance, it's a technical
             impossibility.`,
    betT: amount => `Claiming the ${amount} bet`,
    betP1: 'Write to us at ',
    betP2: ' describing the method you used to remove the filter. The conditions are set out in the ',
    betLink: 'refund policy',
    gdprT: 'Data protection',
    gdprP: `For any request to access, correct or delete your data, the same
            address is enough. We answer within one month at the very most, as
            European regulation requires — in practice, far sooner.`
  },

  mentions: {
    title: 'Legal notice',
    updated: '28 July 2026',
    intro: 'Information about the publisher and the host of the SELFCNTRL website.',
    publisherT: 'Website publisher',
    name: 'Company name',
    form: 'Legal form',
    capital: 'Share capital',
    address: 'Registered office',
    registration: 'Registration',
    vat: 'EU VAT number',
    director: 'Publication director',
    contact: 'Contact',
    hostT: 'Host',
    hostName: 'Name',
    hostAddress: 'Address',
    hostPhone: 'Phone',
    ipT: 'Intellectual property',
    ipP: `Every element of this site — text, interface, code, the SELFCNTRL
          brand — is protected by intellectual property law. Any reproduction
          or reuse, even partial, without prior written permission is
          forbidden.`,
    artT: 'Illustrations',
    artP1: 'Some illustrations used on this site are the work of third parties. Any request concerning their use can be sent to ',
    artP2: ', and will be handled without delay.',
    reportT: 'Reporting',
    reportP: `In accordance with French law on confidence in the digital economy,
              any manifestly illegal content can be reported to the address
              above.`
  },

  cgv: {
    title: 'Terms and conditions',
    updated: '26 September 2026',
    intro: `These terms govern the use of the SELFCNTRL service and the sale of
            subscriptions. By creating an account, you accept them.`,
    s1T: '1. Purpose',
    s1P: `SELFCNTRL is a content filtering service that blocks access to
          pornographic sites on the devices you entrust to it, and makes that
          block deliberately hard to remove. The service is intended
          exclusively for adults who wish to restrict their own access.`,
    s2T: '2. Account',
    s2P: `Creating an account requires a valid email address and a password. You
          are responsible for keeping that password confidential and for
          actions taken from your account. An account is personal: it cannot be
          shared or transferred.`,
    s3T: '3. Nature of the lock',
    s3P: <><b>The service is designed so it cannot be switched off on a whim.</b> That is its function, not a defect. Concretely:</>,
    s3Items: hours => [
      'filtering applies continuously on locked devices;',
      `removing a profile opens a ${hours}-hour cooling-off period before the uninstall password is given;`,
      'on a gradual taper, the number of locked days per week can be increased at any time, but never decreased below the step already reached;',
      'support can neither shorten these delays nor bypass these rules, however insistent you are.'
    ],
    s3After: 'By subscribing, you acknowledge that you understand and want these limitations.',
    s4T: '4. Price and payment',
    s4P: `Two plans are offered: a monthly subscription and a yearly
          subscription. Prices shown are in US dollars, all taxes
          included. Payment is processed by Stripe on a secure page; no bank
          details pass through our servers or are stored by us.`,
    renewT: 'Renewal',
    renewP: `Both subscriptions renew automatically at the end of each period
             — every month for the monthly plan, every year for the yearly
             plan — until cancelled. Cancellation takes effect at the end of the current
             period; no pro rata is refunded for a period already started,
             except in the cases covered by article 6.`,
    s5T: '5. Right of withdrawal',
    s5P: `As the service is digital content supplied immediately, you are
          informed that by requesting its immediate performance you waive your
          statutory fourteen-day right of withdrawal, in accordance with
          article L.221-28 of the French Consumer Code.`,
    s5P2a: <><b>We deliberately go further</b>: see the </>,
    s5Link: 'refund policy',
    s5P2b: days => `, which grants ${days} days with no justification required.`,
    s6T: '6. Cancellation',
    s6P1: `You can cancel at any time from your account, without reason and
           without charge. We may suspend or close an account in the event of
           non-payment, fraudulent use, account sharing, or an attempt to
           circumvent the service for resale purposes.`,
    s6P2: <>On cancellation, filtering stops being applied. <b>Internet access is never cut off</b>: only the blocking rule is disabled.</>,
    s7T: '7. Availability',
    s7P: `We do everything we can to keep the service running, without being
          able to guarantee uninterrupted availability. Filtering relies on
          third-party infrastructure and on your devices' connection: a network
          outage, an operating system change or an action on your part may
          render it temporarily ineffective.`,
    s8T: '8. Liability',
    s8P: `SELFCNTRL is a support tool, not a medical treatment. It replaces
          neither psychological care nor professional advice. We cannot be held
          liable for a relapse, for filtering being circumvented, or for
          indirect consequences of using the service.`,
    s9T: '9. Changes to these terms',
    s9P: `These terms may change. Any substantial modification will be notified
          by email at least thirty days before it takes effect, with the option
          to cancel free of charge.`,
    s10T: '10. Governing law and disputes',
    s10P1: `These terms are governed by French law. In the event of a dispute, an
            amicable solution will be sought first: write to us at `,
    s10P2: `In accordance with article L.612-1 of the French Consumer Code, you
            may use a consumer mediator free of charge. You may also use the
            European online dispute resolution platform.`
  },

  privacy: {
    title: 'Privacy',
    updated: '28 July 2026',
    intro: `What we collect, why, for how long, and above all what we do not
            collect.`,
    notT: 'What we do not collect',
    notP1: <><b>We keep no browsing history whatsoever.</b> Filtering works by whole categories at DNS level: the system knows a category is blocked, it does not keep a list of the sites you tried to open. Nobody here can know what you looked at — or tell you, if you asked.</>,
    notP2: `We neither receive nor store any bank details. We sell no data, to
            anyone, ever.`,
    doT: 'What we collect',
    accountT: 'To run your account',
    accountItems: [
      'your email address, and a display name derived from it;',
      'your password, stored in an irreversibly hashed form;',
      'your counter of days held, your record and the number of relapses declared;',
      'your goals and their progress;',
      'the name, type and technical identifier of the devices you lock;',
      'your cut-off schedules and your quitting rhythm.'
    ],
    payT: 'For payment',
    payItems: [
      'your customer identifier with our payment provider, the status and end date of your subscription.'
    ],
    adsT: 'To measure our advertising',
    adsItems: [
      'the advertising click identifier, your IP address and your browser at the time of signup, kept in order to attribute a purchase to a campaign.'
    ],
    optT: 'If you choose to take part',
    optItems: [
      <>the nickname you choose for the leaderboard. <b>Your first name and your email address never appear there</b>, and participation can be withdrawn in one click.</>
    ],
    basisT: 'Legal bases',
    basisP: `Account and payment data are processed to perform the contract
             between us. Advertising data relies on your consent, revocable at
             any time. Taking part in the leaderboard relies on your explicit
             consent.`,
    whoT: 'Who receives this data',
    processors: {
      stripe: {
        role: 'Payment and subscription management',
        data: 'Email address, card details (never transmitted to our servers), billing history',
        where: 'European Union and United States'
      },
      cloudflare: {
        role: 'DNS filtering of protected devices',
        data: 'DNS requests from locked devices, attached to an anonymous technical identifier',
        where: 'Worldwide network'
      },
      meta: {
        role: 'Advertising effectiveness measurement',
        data: 'Email address in one-way hashed form, purchase amount and date',
        where: 'European Union and United States'
      },
      host: {
        role: 'Hosting of the website and the database',
        data: 'All account data',
        where: 'France'
      }
    },
    hosting: where => `Hosting: ${where}.`,
    transferP: `Transfers outside the European Union are covered by the European
                Commission's standard contractual clauses.`,
    keepT: 'Retention periods',
    keepItems: [
      'Account data: as long as your account exists, then thirty days after deletion;',
      'Invoices: ten years, as accounting law requires;',
      'Advertising data: thirteen months maximum;',
      'Login sessions: seven days.'
    ],
    rightsT: 'Your rights',
    rightsP1: `You have the right to access, rectify, erase, restrict, object to
               and port your data. One address for all of it: `,
    rightsP2: `. We answer within one month at the very most, in practice far
               sooner.`,
    rightsP3: `You may also lodge a complaint with the French data protection
               authority (CNIL).`,
    cookiesT: 'Cookies and analytics',
    cookiesP: `The site sets a session identifier necessary for you to log in,
               which does not require consent. Advertising trackers are only
               activated after you agree, and refusing takes nothing away from
               how the service works.`,
    secT: 'Security',
    secP: `Traffic is encrypted, passwords are stored irreversibly, and access to
           data is limited to the people who need it. As no system is
           infallible, we would inform you without delay in the event of a data
           breach affecting you.`
  },

  refund: {
    title: 'Refunds and guarantee',
    updated: '26 September 2026',
    intro: (days, amount) =>
      `${days} days to change your mind without having to justify yourself, and
       ${amount} if you manage to remove the lock by your own means.`,
    refundT: days => `${days}-day refund`,
    refundP1a: days => `If the service isn't right for you, write to us within
      ${days} days of your payment at `,
    refundP1b: <>. <b>No justification is asked of you</b> and the refund is full. It applies to the first payment of either subscription, monthly or yearly.</>,
    refundP2: `The refund is issued to the original payment method within
               fourteen days, usually far sooner.`,
    betT: amount => `The ${amount} bet`,
    betP: amount => <>If you manage to remove filtering from a device we locked, by your own means, we pay you {amount} <b>on top of</b> a full refund of your subscription.</>,
    countsT: 'What counts',
    countsItems: [
      'uninstalling the configuration profile without using a password we gave you;',
      'permanently disabling filtering while keeping the device supervised;',
      'reaching a site in a blocked category from the locked device, without changing its network configuration.'
    ],
    notCountsT: "What doesn't count",
    notCountsItems: hours => [
      'using another device that isn\'t locked;',
      'factory-resetting the device, which erases everything, including your data;',
      `using the uninstall password obtained at the end of the ${hours}-hour delay — that's normal operation, not a workaround;`,
      'cancelling the subscription, which deliberately disables filtering;',
      'a device that was never supervised, or whose supervision failed.'
    ],
    claimT: 'How to claim',
    claimP: delay => `Write to us describing precisely the method used, with
      enough detail to reproduce it. We answer within ${delay} and carry out
      the verification. If the method works, you get paid — and we fix the
      hole.`,
    limitsT: 'Limits',
    limitsItems: [
      'one claim per person and per account;',
      'the device must have been locked through SELFCNTRL and the subscription active at the time of the attempt;',
      'the method must be reproducible: a workaround caused by a temporary outage at one of our providers does not count.'
    ],
    notT: "What a refund doesn't do",
    notP1: <>Asking for a refund ends your subscription and stops filtering, but <b>does not remove the profile already installed on your devices</b>. For that you have to go through the removal request and its </>,
    notP2: hours => `${hours}-hour delay, described in the `,
    notLink: 'terms and conditions'
  }
}

export default legal
